import type { DeviceFilter, DeviceStatus } from '@/types';
import type { ShellJobDeviceFilter } from './shell-job.service';

/** A DeviceFilter whose status may be any string (the shell-job filter shape). */
export type FacetFilterLike = Omit<DeviceFilter, 'status'> & { status?: string };

// Values MUST match the backend last_online switch verbatim (unknown = no-op).
// Shared by the Devices page facet menu and the Bulk shell filter bar.
export const LAST_SEEN_OPTIONS: { value: string; label: string }[] = [
  { value: '1h', label: 'Active in last hour' },
  { value: '24h', label: 'Active in last 24h' },
  { value: '7d', label: 'Active in last 7d' },
  { value: '30d', label: 'Active in last 30d' },
  { value: 'inactive_7d', label: 'Inactive >7d' },
  { value: 'inactive_30d', label: 'Inactive >30d' },
];

/**
 * The /devices URL params for a facet filter, in the shape the Devices page
 * reads back (arrays as CSV, scalars omitted when empty), so a link carries
 * the same selection over.
 */
export function devicesPageSearch(filter: FacetFilterLike): string {
  const params = new URLSearchParams();
  if (filter.search) params.set('search', filter.search);
  if (filter.status) params.set('status', filter.status);
  const csv = (key: string, v?: string[]) => v?.length && params.set(key, v.join(','));
  csv('type', filter.type);
  csv('sub_type', filter.sub_type);
  csv('model', filter.model);
  csv('os_version', filter.os_version);
  csv('agent_version', filter.agent_version);
  csv('rom_version', filter.rom_version);
  if (filter.is_internal !== undefined) params.set('is_internal', String(filter.is_internal));
  if (filter.last_seen) params.set('last_seen', filter.last_seen);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s);

/**
 * Human summary of a facet filter, e.g. "Modality Rower, Treadmill · Agent 2.4.1".
 * `typeLabel` maps a raw type code to its display name (displayDeviceType).
 */
export function describeDeviceFilter(
  filter: FacetFilterLike,
  typeLabel: (code: string) => string,
): string {
  const parts: string[] = [];
  if (filter.status) parts.push(`Status ${capitalize(filter.status)}`);
  if (filter.type?.length) {
    parts.push(`Modality ${[...new Set(filter.type.map((c) => capitalize(typeLabel(c))))].join(', ')}`);
  }
  if (filter.sub_type?.length) parts.push(`Sub-type ${filter.sub_type.join(', ')}`);
  if (filter.model?.length) parts.push(`Model ${filter.model.join(', ')}`);
  if (filter.os_version?.length) parts.push(`OS ${filter.os_version.join(', ')}`);
  if (filter.agent_version?.length) parts.push(`Agent ${filter.agent_version.join(', ')}`);
  if (filter.rom_version?.length) parts.push(`ROM ${filter.rom_version.join(', ')}`);
  if (filter.last_seen) {
    parts.push(LAST_SEEN_OPTIONS.find((o) => o.value === filter.last_seen)?.label ?? `Last seen ${filter.last_seen}`);
  }
  if (filter.is_internal === false) parts.push('Internal excluded');
  if (filter.is_internal === true) parts.push('Internal only');
  if (filter.search) parts.push(`Search "${filter.search}"`);
  return parts.join(' · ');
}

/** The shell-job filter as a DeviceFilter for the /devices endpoints. */
export function toDeviceFilter(f: ShellJobDeviceFilter, tenantId?: string): DeviceFilter {
  return {
    tenant_id: tenantId || undefined,
    status: (f.status || undefined) as DeviceStatus | undefined,
    type: f.type?.length ? f.type : undefined,
    sub_type: f.sub_type?.length ? f.sub_type : undefined,
    model: f.model?.length ? f.model : undefined,
    os_version: f.os_version?.length ? f.os_version : undefined,
    agent_version: f.agent_version?.length ? f.agent_version : undefined,
    rom_version: f.rom_version?.length ? f.rom_version : undefined,
    is_internal: f.is_internal,
    last_seen: f.last_seen || undefined,
    search: f.search || undefined,
  };
}

/** True when any facet narrows the set (empty arrays / strings don't count). */
export function hasFacetFilter(f: ShellJobDeviceFilter): boolean {
  return (
    !!f.status || !!f.last_seen || !!f.search || f.is_internal !== undefined ||
    (['type', 'sub_type', 'model', 'os_version', 'agent_version', 'rom_version'] as const).some((k) => f[k]?.length)
  );
}

/** Drops empty values so the request body / stored filter stays minimal. */
export function compactFacetFilter(f: ShellJobDeviceFilter): ShellJobDeviceFilter {
  const out: ShellJobDeviceFilter = {};
  if (f.status) out.status = f.status;
  for (const k of ['type', 'sub_type', 'model', 'os_version', 'agent_version', 'rom_version'] as const) {
    if (f[k]?.length) out[k] = f[k];
  }
  if (f.is_internal !== undefined) out.is_internal = f.is_internal;
  if (f.last_seen) out.last_seen = f.last_seen;
  if (f.search) out.search = f.search;
  return out;
}
